import { useCallback, useEffect, useRef, useState } from "react";
import { getMe, type MeResponse } from "../api/me";
import { HttpError } from "../api/errors";

export type UseMeResult = {
  data: MeResponse | null;
  loading: boolean;
  error: (Error & { status?: number }) | null;
  refresh: () => void;
};

export { HttpError };

export function useMe(): UseMeResult {
  const [data, setData] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);
  const mountedRef = useRef(true);

  const refresh = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    getMe(ctrl.signal).then(
      (res) => {
        if (!mountedRef.current) return;
        setData(res);
        setLoading(false);
      },
      (err: Error) => {
        if (!mountedRef.current) return;
        if (err.name === "AbortError") return;
        setData(null);
        setError(err);
        setLoading(false);
      },
    );
    return () => {
      mountedRef.current = false;
      ctrl.abort();
    };
  }, [tick]);

  return { data, loading, error, refresh };
}
